// src/services/alertService.ts
import { ReactNode } from "react";
import Swal, { SweetAlertIcon, SweetAlertResult } from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./alert.css";
import { Box, Typography } from "@mui/material";

// ✅ ใช้ i18n instance ตรง ๆ (ไม่ใช้ useTranslation ใน service)
import i18n, { common, input } from "../../i18n";

// ถ้าโปรเจ็กต์คุณไม่ได้ export namespace แบบนี้จาก ../i18n
// ให้แก้เป็น ["common","input"] ด้านล่างได้เลย
// import { common, input } from "../i18n";

// ---- helper แปลเป็น string เสมอ ----
const tStr = (key: string | undefined, options?: any): string | undefined => {
  if (!key) return undefined;
  // ใส่ ns เองถ้าจำเป็น: ns: ["common","input"]
  return i18n.t(key, { ns: [common, input], ...options }) as string;
};

const MySwal = withReactContent(Swal);

let _loadingCount = 0;

const alertService = {
  success: async (message: string, title?: string) => {
    await MySwal.fire({
      title: tStr(title) ?? tStr("Successfully"),
      text: tStr(message),
      icon: "success",
      showCloseButton: true,
      confirmButtonColor: "var(--dark)",
      confirmButtonText: tStr("Close")!,
    });
  },

  error: async (message: string, title?: string, html?: any) => {
    await MySwal.fire({
      title: tStr(title) ?? tStr("Error-Message"),
      text: tStr(message),
      icon: "error",
      showCloseButton: true,
      confirmButtonText: tStr("Close")!,
      confirmButtonColor: "var(--dark)",
      html,
    });
  },

  warning: (message: string, title?: string) => {
    MySwal.fire({
      title: tStr(title) ?? "Warning",
      text: tStr(message),
      icon: "warning",
      showCloseButton: true,
      confirmButtonColor: "var(--dark)",
    });
  },

  info: async (message: string, title?: string) => {
    await MySwal.fire({
      title: tStr(title), // อนุญาต undefined
      text: tStr(message),
      icon: "info",
      showConfirmButton: false,
      showCloseButton: true,
    });
  },

  confirm: (
    title: any,
    body: any,
    confirmButtonText?: string,
    confirmButtonColor?: "var(--red)" | "var(--dark)",
    showConfirmButton?: boolean,
    width?: string | number | undefined,
    cancelButtonText?: string,
    cancelButtonColor?: "var(--red)" | "var(--white)",
    allowOutsideClick = true,
    showCloseButton = true,
  ) => {
    const isOutlinedCancel = cancelButtonColor === "var(--white)";

    return MySwal.fire({
      showCloseButton,
      confirmButtonText: tStr(confirmButtonText) ?? tStr("Yes")!,
      showConfirmButton: showConfirmButton ?? true,
      width,
      showCancelButton: !!cancelButtonText,
      cancelButtonText: tStr(cancelButtonText),

      // ปุ่ม Confirm ใช้สีตามที่ส่งมา (ถ้าไม่ส่งใช้ var(--dark))
      confirmButtonColor: confirmButtonColor || "var(--dark)",

      // ถ้าเป็น outlined (white) ไม่ต้องส่งสี ให้ไปใช้ class แทน
      cancelButtonColor: isOutlinedCancel ? undefined : cancelButtonColor,

      allowOutsideClick: allowOutsideClick,

      customClass: {
        // ถ้าเป็น white ⇒ outlined, ถ้าอย่างอื่น ⇒ solid
        cancelButton: isOutlinedCancel
          ? "cancel-btn-outlined"
          : "cancel-btn-solid",
      },

      html: (
        <Box display="flex" flexDirection="column" gap="24px">
          <Typography
            sx={{ fontSize: 20, fontWeight: "bold", textAlign: "left" }}
          >
            {typeof title === "string" ? tStr(title) : title}
          </Typography>
          <Typography
            sx={{
              fontSize: 16,
              lineHeight: "24px",
              fontWeight: 500,
              textAlign: "left",
              whiteSpace: "pre-line",
            }}
          >
            {typeof body === "string" ? tStr(body) : body}
          </Typography>
        </Box>
      ),
    }).then((v: any) => v.isConfirmed);
  },

  // two answers plus ×; button texts arrive already translated
  choose: (
    title: ReactNode,
    body: ReactNode,
    confirmButtonText: string,
    denyButtonText: string,
  ): Promise<"confirm" | "deny" | "dismiss"> =>
    MySwal.fire({
      showCloseButton: true,
      showConfirmButton: true,
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText,
      denyButtonText,
      confirmButtonColor: "var(--dark)",
      allowOutsideClick: true,
      customClass: { denyButton: "cancel-btn-outlined" },
      html: (
        <Box display="flex" flexDirection="column" gap="24px">
          <Typography
            sx={{ fontSize: 20, fontWeight: "bold", textAlign: "left" }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: 16,
              lineHeight: "24px",
              fontWeight: 500,
              textAlign: "left",
              whiteSpace: "pre-line",
            }}
          >
            {body}
          </Typography>
        </Box>
      ),
    }).then((v: SweetAlertResult) =>
      v.isConfirmed ? "confirm" : v.isDenied ? "deny" : "dismiss",
    ),

  selection: (
    title: string,
    body: string,
    confirmButtonText?: string,
    confirmButtonColor?: string,
    showConfirmButton?: boolean,
    width?: string | number | undefined,
    cancelButtonText?: string,
    cancelButtonColor?: string,
  ) => {
    return MySwal.fire({
      showCloseButton: true,
      showConfirmButton: showConfirmButton ?? true,
      showCancelButton: true,
      width: width || 480,
      buttonsStyling: false,
      reverseButtons: false,

      confirmButtonText: tStr(confirmButtonText) ?? tStr("Yes")!,
      cancelButtonText: tStr(cancelButtonText) ?? tStr("No")!,

      html: (
        <Box display="flex" flexDirection="column" textAlign="left">
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "var(--dark)",
              mb: "12px",
            }}
          >
            {tStr(title)}
          </Typography>
          <Typography
            sx={{ fontSize: 16, lineHeight: "24px", color: "var(--gray-dark)" }}
          >
            {tStr(body)}
          </Typography>
        </Box>
      ),

      customClass: {
        popup: "unsaved-modal",
        actions: "unsaved-actions",
        confirmButton: "unsaved-btn-confirm",
        cancelButton: "unsaved-btn-cancel",
        closeButton: "unsaved-close",
      },
    }).then((v: any) => v.isConfirmed);
  },

  mixin: (title: string, icon: SweetAlertIcon = "success") => {
    const Toast = Swal.mixin({
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast: any) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      },
      padding: 16,
    });

    return Toast.fire({
      icon,
      title: tStr(title),
    });
  },

  showLoading: () => {
    _loadingCount++;
    if (_loadingCount === 1) {
      Swal.fire({
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    }
  },

  closeLoading: () => {
    _loadingCount = Math.max(0, _loadingCount - 1);
    if (_loadingCount === 0 && Swal.isLoading()) {
      Swal.close();
    }
  },
};

export default alertService;
